/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2020 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2020 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { dateUtils } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  mapSelectors,
  mapActions,
  uiSelectors,
  layerSelectors,
  genericActions,
  mapEnums,
  syncGroupsSelectors,
} from '@opengeoweb/store';
import type { TimeBounds } from '@opengeoweb/time-slider';
import {
  secondsPerPxFromCanvasWidth,
  getNewCenterOfFixedPointZoom,
  TimeSlider,
  defaultTimeSpan,
  onsetNewDateDebounced,
  getFilteredTime,
  moveSelectedTimePx,
  defaultSecondsPerPx,
} from '@opengeoweb/time-slider';
import { handleDateUtilsISOString } from '@opengeoweb/webmap';
import { TimeSliderButtonsConnect } from './TimeSliderButtonsConnect';
import { TimeSliderCurrentTimeBoxConnect } from './TimeSliderCurrentTimeBoxConnect';
import { TimeSliderLegendConnect } from './TimeSliderLegendConnect';
import {
  calculateNewAnimationTimeBounds,
  getTimeBounds,
} from './timesliderUtils';

/** Below dialogs, so their resize handles stay hoverable over the slider. */
export const TIME_SLIDER_Z_INDEX = 60;

export const useUpdateTimestep = (mapId: string): void => {
  const autoTimeStepLayerId = useSelector((store: CoreAppStore) =>
    mapSelectors.getAutoTimeStepLayerId(store, mapId),
  );
  const isTimeStepAuto = useSelector((store: CoreAppStore) =>
    mapSelectors.isTimestepAuto(store, mapId),
  );
  const timeStep = useSelector((store: CoreAppStore) =>
    layerSelectors.getTimeStepForLayerId(store, autoTimeStepLayerId),
  );
  const dispatch = useDispatch();
  React.useEffect(() => {
    if (isTimeStepAuto) {
      if (timeStep !== undefined) {
        dispatch(mapActions.setTimeStep({ mapId, timeStep }));
      }
    }
  }, [autoTimeStepLayerId, dispatch, isTimeStepAuto, mapId, timeStep]);
};

export const useUpdateTimeSpan = (
  mapId: string,
  myOnSetTimeSliderSpan: (newSpan: number, newCenterTime: number) => void,
): void => {
  const autoTimeStepLayerId = useSelector((store: CoreAppStore) =>
    mapSelectors.getAutoTimeStepLayerId(store, mapId),
  );
  const isTimeSpanAuto = useSelector((store: CoreAppStore) =>
    mapSelectors.isTimeSpanAuto(store, mapId),
  );
  const activeLayerTimeDimension = useSelector((store: CoreAppStore) =>
    layerSelectors.getLayerTimeDimension(store, autoTimeStepLayerId),
  );
  const hasActiveLayerTimeDimension = Boolean(activeLayerTimeDimension);
  const timeSliderWidth = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapTimeSliderWidth(store, mapId),
  );
  const centerTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapTimeSliderCenterTime(store, mapId),
  );
  const secondsPerPx = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapTimeSliderSecondsPerPx(store, mapId),
  );
  const currentTimeSpan = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapTimeSliderSpan(store, mapId),
  );

  const selectedTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getSelectedTime(store, mapId),
  );

  const isAnimating = useSelector((store: CoreAppStore) =>
    mapSelectors.isAnimating(store, mapId),
  );

  const [now, setNow] = React.useState(() => Math.floor(Date.now() / 1000));
  const updateNow = React.useCallback((): void => {
    setNow(Math.floor(Date.now() / 1000));
  }, []);

  React.useEffect(() => {
    if (
      !isTimeSpanAuto ||
      !autoTimeStepLayerId ||
      !hasActiveLayerTimeDimension
    ) {
      return undefined;
    }
    updateNow();
    const intervalId = window.setInterval(updateNow, 60 * 1000);
    return (): void => window.clearInterval(intervalId);
  }, [
    autoTimeStepLayerId,
    hasActiveLayerTimeDimension,
    isTimeSpanAuto,
    updateNow,
  ]);

  const updateTimeSliderSpan = React.useCallback(
    (spanInSeconds: number, newCenterTime: number): void => {
      // Do not dispatch actions if values are the same
      if (
        spanInSeconds !== undefined &&
        newCenterTime !== undefined &&
        (currentTimeSpan !== spanInSeconds || centerTime !== newCenterTime)
      ) {
        myOnSetTimeSliderSpan(spanInSeconds, newCenterTime);
      }
    },
    [centerTime, currentTimeSpan, myOnSetTimeSliderSpan],
  );

  React.useEffect(() => {
    if (isTimeSpanAuto && isAnimating) {
      return;
    }
    if (
      isTimeSpanAuto &&
      autoTimeStepLayerId &&
      activeLayerTimeDimension &&
      !isAnimating
    ) {
      const { startTime, endTime }: TimeBounds = getTimeBounds([
        activeLayerTimeDimension!,
      ]);
      const oneDayInSeconds = 24 * 3600;
      let slicedStartTime: number;
      let slicedEndTime: number;
      if (now < startTime) {
        slicedStartTime = now;
        slicedEndTime = now + oneDayInSeconds;
      } else if (now > endTime) {
        slicedStartTime = now - oneDayInSeconds;
        slicedEndTime = now;
      } else {
        slicedStartTime = Math.max(startTime, now - oneDayInSeconds);
        slicedEndTime = now;
      }
      const spanInSeconds = slicedEndTime - slicedStartTime;
      const newCenterTime = (slicedStartTime + slicedEndTime) / 2;
      updateTimeSliderSpan(spanInSeconds, newCenterTime);
    } else {
      const spanInSeconds = currentTimeSpan || defaultTimeSpan;
      const newCenterTime = getNewCenterOfFixedPointZoom(
        selectedTime,
        secondsPerPx!,
        secondsPerPxFromCanvasWidth(timeSliderWidth!, spanInSeconds)!,
        centerTime!,
      );
      updateTimeSliderSpan(spanInSeconds, newCenterTime!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoTimeStepLayerId,
    isAnimating,
    isTimeSpanAuto,
    mapId,
    activeLayerTimeDimension?.name,
    now,
  ]);
};

const timeToIso = (selectedTime: number): string =>
  handleDateUtilsISOString(dateUtils.fromUnix(selectedTime).toISOString());

interface TimeSliderConnectProps {
  sourceId: string;
  mapId: string;
  isAlwaysVisible?: boolean;
  mapWindowRef?: React.RefObject<HTMLElement | null>;
}

export const TimeSliderConnect: React.FC<TimeSliderConnectProps> = ({
  sourceId,
  mapId,
  isAlwaysVisible = false,
  mapWindowRef,
}: TimeSliderConnectProps) => {
  const isTimeSliderHoverOn = useSelector((store: CoreAppStore) =>
    mapSelectors.isTimeSliderHoverOn(store, mapId),
  );

  const mapIsActive = useSelector((store: CoreAppStore) =>
    uiSelectors.getIsActiveWindowId(store, mapId),
  );
  const isTimeSliderVisible = useSelector((store: CoreAppStore) =>
    mapSelectors.isTimeSliderVisible(store, mapId),
  );

  // don't use redux state if isAlwaysVisible
  const isVisible = isAlwaysVisible || isTimeSliderVisible;

  const dispatch = useDispatch();

  // TODO: move keyboard logic to TimeSlider.tsx
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.ctrlKey && event.altKey && event.code === 'KeyH') {
        dispatch(
          mapActions.toggleTimeSliderHover({
            mapId,
            isTimeSliderHoverOn: !isTimeSliderHoverOn,
          }),
        );
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return (): void => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mapId, isTimeSliderHoverOn, dispatch]);

  const onToggleTimeSliderVisibility = React.useCallback(
    (isTimeSliderVisible: boolean): void => {
      dispatch(
        mapActions.toggleTimeSliderIsVisible({
          mapId,
          isTimeSliderVisible,
          origin: mapEnums.MapActionOrigin.map,
        }),
      );
    },
    [dispatch, mapId],
  );

  const onSetTimeSliderSpan = React.useCallback(
    (newSpan: number): void => {
      dispatch(
        mapActions.setTimeSliderSpan({
          mapId,
          timeSliderSpan: newSpan!,
        }),
      );
    },
    [dispatch, mapId],
  );

  const timeStep = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapTimeStep(store, mapId),
  );

  const [dataStartTime, dataEndTime] = useSelector((store: CoreAppStore) =>
    mapSelectors.getDataLimitsFromLayers(store, mapId),
  );
  const isAnimating = useSelector((store: CoreAppStore) =>
    mapSelectors.isAnimating(store, mapId),
  );

  const selectedTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getSelectedTime(store, mapId),
  );
  const animationStartTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getAnimationStartTime(store, mapId),
  );
  const animationEndTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getAnimationEndTime(store, mapId),
  );

  useUpdateTimestep(mapId);
  useUpdateTimeSpan(mapId, onSetTimeSliderSpan);

  // Local state for unfiltered selected time
  const [unfilteredSelectedTime, setUnfilteredSelectedTime] =
    React.useState<number>(selectedTime);

  // Store unfilteredSelectedTime in a ref for callbacks
  const unfilteredSelectedTimeRef = React.useRef(unfilteredSelectedTime);
  React.useEffect(() => {
    unfilteredSelectedTimeRef.current = unfilteredSelectedTime;
  }, [unfilteredSelectedTime]);

  // Reference to the last time value set by this timeslider
  const lastTimeValueAsSetByThisTimeSlider = React.useRef<string>(undefined);

  // Track the last selectedTime we processed to avoid unnecessary updates
  const lastProcessedSelectedTime = React.useRef<number>(selectedTime);

  // Set new time in the redux state
  const onSetNewDate = React.useCallback(
    (newDate: string): void => {
      // Remember that this timeslider has set this time
      lastTimeValueAsSetByThisTimeSlider.current = newDate;
      if (isAnimating) {
        dispatch(mapActions.mapStopAnimation({ mapId }));
      }

      dispatch(
        genericActions.setTime({
          sourceId,
          origin: 'TimeSliderConnect',
          value: { currentTime: handleDateUtilsISOString(newDate) },
        }),
      );
    },
    [dispatch, isAnimating, mapId, sourceId],
  );

  // Update local state when time is updated outside of this component, e.g. during animation or syncgroups
  React.useEffect(() => {
    // Only update if selectedTime has actually changed (not just timeStep)
    if (lastProcessedSelectedTime.current === selectedTime) {
      return;
    }

    const selectedTimeAsIsoString = timeToIso(selectedTime);

    // Check if this is our own update bouncing back
    const isOurOwnUpdate =
      selectedTimeAsIsoString === lastTimeValueAsSetByThisTimeSlider.current;

    if (isOurOwnUpdate) {
      // Our update was processed, clear the flag and update tracking
      lastTimeValueAsSetByThisTimeSlider.current = undefined;
      lastProcessedSelectedTime.current = selectedTime;
    } else {
      // Time changed from outside this component (e.g., backward/forward buttons, animation)
      // Cancel any pending debounced updates and sync local state
      onsetNewDateDebounced.cancel();
      lastProcessedSelectedTime.current = selectedTime;
      setUnfilteredSelectedTime(selectedTime);
      // Clear the flag so we don't block future external updates to this same time
      lastTimeValueAsSetByThisTimeSlider.current = undefined;
    }
  }, [selectedTime]);

  // Store timeStep in a ref so we can use the latest value without causing re-renders
  const timeStepRef = React.useRef(timeStep);
  const prevTimeStepRef = React.useRef(timeStep);

  React.useEffect(() => {
    const timeStepChanged = prevTimeStepRef.current !== timeStep;
    prevTimeStepRef.current = timeStep;
    timeStepRef.current = timeStep;

    // When timestep changes, apply smart rounding to current time
    // For large timesteps (>12h), round to nearest hour instead of timestep
    if (timeStepChanged) {
      const roundingStep = timeStep > 12 * 60 ? 60 : timeStep;
      const snappedTime = getFilteredTime(
        unfilteredSelectedTime,
        roundingStep,
        dataStartTime,
        dataEndTime,
      );
      if (snappedTime !== unfilteredSelectedTime) {
        setUnfilteredSelectedTime(snappedTime);
        onsetNewDateDebounced(timeToIso(snappedTime), onSetNewDate);
      }
    }
  }, [
    timeStep,
    unfilteredSelectedTime,
    dataStartTime,
    dataEndTime,
    onSetNewDate,
  ]);

  // Function used by timeslider components to update the unfiltered selected time
  const setUnfilteredSelectedTimeFn = React.useCallback(
    (unfilteredSelectedTimeFromComponents: number): void => {
      // Only update if the value has actually changed
      if (
        unfilteredSelectedTimeFromComponents ===
        unfilteredSelectedTimeRef.current
      ) {
        return;
      }

      // Snap to nearest timestep when clicking/dragging on timeslider
      // For timesteps >= 1 hour, snap to nearest hour
      // For smaller timesteps, snap to the timestep itself (e.g., 5min, 10min, 15min)
      const roundingStep = timeStepRef.current >= 60 ? 60 : timeStepRef.current;
      const timeToSet = getFilteredTime(
        unfilteredSelectedTimeFromComponents,
        roundingStep,
        dataStartTime,
        dataEndTime,
      );

      setUnfilteredSelectedTime(timeToSet);
      onsetNewDateDebounced(timeToIso(timeToSet), onSetNewDate);
    },
    [dataEndTime, dataStartTime, onSetNewDate],
  );

  // Move animation bar when user changes time using time picker
  // Animation bar however cannot be moved into a time where there is no data
  const moveAnimationUsingCalendar = (newTime: number): void => {
    if (
      !animationEndTime ||
      !animationStartTime ||
      !dataStartTime ||
      !dataEndTime
    ) {
      return;
    }

    const formatToIsoString = (date: number): string =>
      handleDateUtilsISOString(new Date(date).toISOString());

    const { newAnimationStartTime, newAnimationEndTime } =
      calculateNewAnimationTimeBounds(
        newTime,
        animationStartTime,
        animationEndTime,
        dataStartTime,
        dataEndTime,
      );

    dispatch(
      mapActions.setAnimationStartTime({
        mapId,
        animationStartTime: formatToIsoString(newAnimationStartTime),
      }),
    );
    dispatch(
      mapActions.setAnimationEndTime({
        mapId,
        animationEndTime: formatToIsoString(newAnimationEndTime),
      }),
    );
  };

  const onCalendarSelect = (newSelectedTime: number): void => {
    // When user explicitly sets time via calendar, snap to nearest available timestep
    // For large timesteps (>12h), round to nearest hour instead of timestep
    const roundingStep = timeStep > 12 * 60 ? 60 : timeStep;
    const snappedTime = getFilteredTime(
      newSelectedTime,
      roundingStep,
      dataStartTime,
      dataEndTime,
    );
    setUnfilteredSelectedTime(snappedTime);
    onsetNewDateDebounced(timeToIso(snappedTime), onSetNewDate);
    moveAnimationUsingCalendar(snappedTime);
  };

  const secondsPerPx =
    useSelector((store: CoreAppStore) =>
      mapSelectors.getMapTimeSliderSecondsPerPx(store, mapId),
    ) || defaultSecondsPerPx;
  const timeSliderWidth =
    useSelector((store: CoreAppStore) =>
      mapSelectors.getMapTimeSliderWidth(store, mapId),
    ) || 0;
  const centerTime = useSelector((store: CoreAppStore) =>
    mapSelectors.getMapTimeSliderCenterTime(store, mapId),
  );
  const isTimeScrollingEnabled = useSelector(
    syncGroupsSelectors.isTimeScrollingEnabled,
  );

  const adjustSelectedTimeOnWheel = React.useCallback(
    ({
      event,
      deltaY,
    }: {
      event: WheelEvent | KeyboardEvent;
      deltaY: number;
    }): void => {
      if (!(event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)) {
        if (deltaY === 0) {
          return;
        }
        // timeStepRef.current is in minutes, convert to seconds
        const timeStepInSeconds = timeStepRef.current! * 60;
        const pixelsPerScroll =
          (-deltaY / Math.abs(deltaY)) * (timeStepInSeconds / secondsPerPx);
        moveSelectedTimePx(
          pixelsPerScroll,
          timeSliderWidth,
          centerTime!,
          dataStartTime!,
          dataEndTime!,
          secondsPerPx,
          timeStepRef.current!,
          unfilteredSelectedTimeRef.current,
          setUnfilteredSelectedTimeFn,
        );
      }
    },
    [
      timeSliderWidth,
      centerTime,
      dataEndTime,
      dataStartTime,
      secondsPerPx,
      setUnfilteredSelectedTimeFn,
    ],
  );

  React.useEffect(() => {
    if (isTimeScrollingEnabled && mapWindowRef?.current) {
      const handleWheel = (event: WheelEvent): void => {
        adjustSelectedTimeOnWheel({ event, deltaY: event.deltaY });
      };
      const element = mapWindowRef.current;
      element.addEventListener('wheel', handleWheel);
      return (): void => {
        element.removeEventListener('wheel', handleWheel);
      };
    }
    return undefined;
  }, [isTimeScrollingEnabled, mapWindowRef, adjustSelectedTimeOnWheel]);

  return (
    <TimeSlider
      buttons={
        <TimeSliderButtonsConnect
          mapId={mapId}
          mapIsActive={mapIsActive}
          sourceId={sourceId}
        />
      }
      dataEndTime={dataEndTime}
      dataStartTime={dataStartTime}
      isVisible={isVisible}
      legend={
        <TimeSliderLegendConnect
          adjustSelectedTimeOnWheel={adjustSelectedTimeOnWheel}
          mapId={mapId}
          mapWindowRef={mapWindowRef}
          onSetTimeSliderSpan={onSetTimeSliderSpan}
          setUnfilteredSelectedTime={setUnfilteredSelectedTimeFn}
          unfilteredSelectedTime={unfilteredSelectedTime}
        />
      }
      mapIsActive={mapIsActive}
      onSetCenterTime={(newTime: number): void => {
        dispatch(
          mapActions.setTimeSliderCenterTime({
            mapId,
            timeSliderCenterTime: newTime,
          }),
        );
      }}
      onSetNewDate={(newDate: string): void => {
        onsetNewDateDebounced(newDate, onSetNewDate);
      }}
      onToggleTimeSlider={onToggleTimeSliderVisibility}
      selectedTime={selectedTime}
      timeBox={
        <TimeSliderCurrentTimeBoxConnect
          mapId={mapId}
          onCalendarSelect={onCalendarSelect}
          setUnfilteredSelectedTime={setUnfilteredSelectedTimeFn}
          unfilteredSelectedTime={unfilteredSelectedTime}
        />
      }
      timeStep={timeStep}
    />
  );
};
